import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ValidatorForm } from "react-material-ui-form-validator";
import { InputField, Dropdown } from "../../../../../components/common";
import { endUserActions } from "../../../../../store/endUser/endUserActions";
import { contactsActions } from "../../../../../store/contacts/contactsActions";
import { contactsTypesActions } from "../../../../../store/contactTypes/contactTypesActions";

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: "6px",
    maxWidth: "600px",
    width: "100%",
    maxHeight: "90vh",
    margin: "32px auto",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    fontSize: "22px",
    color: "#fff",
    padding: "15px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#3699FF",
    margin: 0,
    position: "relative",
    minHeight: "70px",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
    lineHeight: 1.2,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "22px",
    color: "#fff",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 600,
    margin: 0,
    flex: 1,
    paddingRight: "40px",
    lineHeight: 1.2,
  },
  closeButton: {
    position: "absolute",
    top: "50%",
    right: "15px",
    transform: "translateY(-50%)",
    color: "white",
    padding: "8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  dialogContent: {
    padding: "0 !important",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  contentWrapper: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    gap: "16px",
    minHeight: 0,
  },
  scrollableBody: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    paddingRight: "8px",

    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c1c1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#a8a8a8",
    },
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "10px",
  },
  formContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    backgroundColor: "#f9f9f9",
  },
  formTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "15px",
  },
  gridWrapper: {
    marginBottom: "0px",
    "& .MuiFormHelperText-root.Mui-error ": {
      background: "unset !important",
      color: " #f44336 !important",
    },
  },
  input: {
    "& input[type=number]": {
      "-moz-appearance": "textfield",
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "16px",
    flexShrink: 0,
    paddingTop: "16px",
    borderTop: "1px solid #e0e0e0",
  },
  cancelButton: {
    textTransform: "none !important",
  },
  prospectInfo: {
    backgroundColor: "#f5f8fa",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "16px",
  },
  prospectName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "5px",
  },
  prospectEmail: {
    fontSize: "14px",
    color: "#666",
  },
  requiredField: {
    color: "#f44336",
    marginLeft: "4px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
  commentSection: {
    marginTop: "16px",
  },
  textArea: {
    width: "100%",
    minHeight: "100px",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    resize: "vertical",
    fontFamily: "inherit",
    "&:focus": {
      outline: "none",
      borderColor: "#3699ff",
    },
  },
}));

const ConvertToCustomerModal = ({
  open,
  setOpen,
  prospectData,
  loadLeadsGeneration,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // Dropdown data states
  const [businessContactTypes, setBusinessContactTypes] = useState([]);
  const [businessGroups, setBusinessGroups] = useState([]);

  // Form states
  const [businessForm, setBusinessForm] = useState({
    companyName: "",
    contactType: "",
    businessGroup: "",
  });

  // Fetch dropdown data when modal opens
  useEffect(() => {
    if (open) {
      fetchDropdownData();
      if (prospectData) {
        setBusinessForm(prev => ({
          ...prev,
          companyName: prospectData.company,
        }));
      }
    }
  }, [open]);

  const fetchDropdownData = async () => {
    setLoadingData(true);
    try {
      // Fetch Business Contact Types (type 2)
      await dispatch(endUserActions.onGetContactTypesDDL(2)).then(data => {
        if (data) {
          setBusinessContactTypes(data?.data || []);
          if (data?.data?.length > 0) {
            setBusinessForm(prev => ({
              ...prev,
              contactType: data.data[0].id.toString(),
            }));
          }
        }
      });

      // Fetch Business Groups
      await dispatch(contactsActions.onGetBusinessGroupDropDown()).then(
        data => {
          if (data) {
            setBusinessGroups(data?.data || []);
            if (data?.data?.length > 0) {
              setBusinessForm(prev => ({
                ...prev,
                businessGroup: data.data[0].id.toString(),
              }));
            }
          }
        }
      );
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load form data");
    } finally {
      setLoadingData(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setComment("");
    setBusinessForm({
      companyName: "",
      contactType: "",
      businessGroup: "",
    });
  };

  const handleCommentChange = event => {
    setComment(event.target.value);
  };

  const handleBusinessFormChange = (field, value) => {
    setBusinessForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    if (
      !businessForm.companyName ||
      !businessForm.contactType ||
      !businessForm.businessGroup
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);

    try {
      const formData = {
        prospectId: prospectData?.id,
        accountType: "BusinessCustomer",
        companyName: businessForm.companyName,
        groupId: businessForm.businessGroup,
        contactTypeId: businessForm.contactType,
        comments: comment.trim(),
      };

      console.log("Submitting conversion data:", formData);

      const response = await dispatch(
        contactsTypesActions.onConvertProspect(formData)
      );
      if (response?.data?.success) {
        toast.success("Prospect successfully converted to Business User");
        loadLeadsGeneration();
        handleClose();
      } else {
        toast.error(response?.data?.message || "Failed to convert prospect");
      }
    } catch (error) {
      console.error("Error converting prospect:", error);
      toast.error("Failed to convert prospect. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Render Business User Form
  const renderBusinessUserForm = () => {
    if (loadingData) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography style={{ marginLeft: "10px" }}>
            Loading form data...
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.formContainer}>
        <div className={classes.formTitle}>
          Business User Information
          <Typography variant='caption' className={classes.requiredField}>
            * Required fields
          </Typography>
        </div>

        <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              className={(classes.gridWrapper, "requiredField")}>
              <InputField
                placeholder='Company Name'
                inputlabel='Company Name'
                className={classes.gridWrapper}
                value={businessForm.companyName}
                showRequired={true}
                onChange={v =>
                  handleBusinessFormChange("companyName", v.target.value)
                }
                validators={["required", "matchRegexp:^.{0,50}$"]}
                errorMessages={[
                  "This field is required",
                  "Maximum length is 50 characters",
                ]}
              />
            </Grid>

            <Grid
              item
              xs={6}
              className={(classes.gridWrapper, "requiredField")}>
              <Dropdown
                selectlabel='Contact Type'
                placeholder='Please Select'
                disabled={false}
                value={`${businessForm.contactType}`}
                required={false}
                showRequired
                customMenuItems={businessContactTypes?.map(b => {
                  return {
                    id: b?.id,
                    name: b?.value,
                  };
                })}
                handleChange={({ target }) => {
                  handleBusinessFormChange("contactType", target.value);
                }}
              />
            </Grid>

            <Grid
              item
              xs={6}
              className={(classes.gridWrapper, "requiredField")}>
              <Dropdown
                selectlabel='Business Group'
                placeholder='Please Select'
                disabled={false}
                value={parseInt(businessForm.businessGroup) || ""}
                required={false}
                showRequired
                customMenuItems={businessGroups?.map(c => {
                  return {
                    id: parseInt(c.id),
                    name: c.value,
                  };
                })}
                handleChange={({ target }) => {
                  handleBusinessFormChange(
                    "businessGroup",
                    target.value.toString()
                  );
                }}
              />
            </Grid>

            {/* Comment Section */}
            <Grid item xs={12}>
              <div className={classes.commentSection}>
                <Typography className={classes.sectionTitle}>
                  Additional Comments (Optional)
                </Typography>
                <textarea
                  className={classes.textArea}
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder='Add any additional comments about this conversion...'
                />
              </div>
            </Grid>
          </Grid>

          {/* Actions inside ValidatorForm */}
          <div className={classes.actions}>
            <Button
              variant='outlined'
              onClick={handleClose}
              className={classes.cancelButton}
              disabled={isLoading || loadingData}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={isLoading || loadingData}>
              {isLoading ? (
                <CircularProgress size={16} />
              ) : (
                "Convert to Business User"
              )}
            </Button>
          </div>
        </ValidatorForm>
      </div>
    );
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      classes={{
        paper: classes.dialogPaper,
      }}
      maxWidth={false}>
      <DialogTitle className={classes.header} disableTypography>
        <Typography variant='h2' className={classes.headerTitle}>
          Convert Prospect to Business User
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size='small'>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.contentWrapper}>
          <div className={classes.scrollableBody}>
            {/* Prospect Information */}
            {prospectData && (
              <div className={classes.prospectInfo}>
                <div className={classes.prospectName}>
                  {prospectData.firstName} {prospectData.lastName}
                </div>
                <div className={classes.prospectEmail}>
                  {prospectData.email}
                </div>
              </div>
            )}

            {/* Business User Form */}
            {renderBusinessUserForm()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToCustomerModal;
